/**
 * Authentication API
 * Login, register, and password management using AWS Cognito
 */

import {
  CognitoUser,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { userPool } from '@/utils/auth';
import type { User, Credentials, RegisterData, AuthResponse } from '@/types/auth';
import { apiRequest } from './client';

const COGNITO_REGION = import.meta.env.VITE_COGNITO_REGION || 'eu-west-2';
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';

/**
 * Login with email and password using direct Cognito API call
 * This matches the pattern used in the React Native app
 */
export const login = async (credentials: Credentials): Promise<AuthResponse> => {
  try {
    console.log('Attempting Cognito authentication...');
    
    // Call Cognito API directly (same as React Native app)
    const response = await fetch(`https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: COGNITO_CLIENT_ID,
        AuthParameters: {
          USERNAME: credentials.email,
          PASSWORD: credentials.password
        }
      })
    });

    const data = await response.json();
    
    if (!response.ok || !data.AuthenticationResult) {
      console.error('Cognito authentication failed:', data);
      
      // Check for UserNotConfirmedException
      const errorType = data.__type || '';
      const errorMessage = data.message || 'Authentication failed';
      
      // Return a clear error message for unverified users
      if (errorType.includes('UserNotConfirmedException') || 
          errorMessage.toLowerCase().includes('not confirmed')) {
        return {
          success: false,
          error: 'UserNotConfirmedException: User is not confirmed.',
        };
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }

    const token = data.AuthenticationResult.IdToken;
    const refreshToken = data.AuthenticationResult.RefreshToken;

    console.log('Cognito authentication successful, fetching teacher profile...');

    // Store token temporarily so apiRequest can use it
    const tempAuthData = {
      idToken: token,
      accessToken: token,
      refreshToken: refreshToken,
    };
    localStorage.setItem('authData', JSON.stringify(tempAuthData));

    // Fetch user profile from Lambda
    try {
      const profileResponse = await apiRequest<{ teacher: User }>('get_teacher_profile', {
        email: credentials.email,
      });

      console.log('Profile response:', profileResponse);

      // Lambda returns {success: true, teacher: {...}} not {success: true, data: {teacher: {...}}}
      if (profileResponse.success && (profileResponse as any).teacher) {
        console.log('Login successful!');
        return {
          success: true,
          user: (profileResponse as any).teacher,
          token,
          refreshToken,
        };
      } else {
        // If teacher profile not found, check if this is a student
        console.log('Teacher profile not found, checking if this is a student...');
        
        // Redirect students to the web app
        const errorMessage = profileResponse.error || '';
        if (errorMessage.includes('not found') || errorMessage.includes('does not exist')) {
          // This might be a student trying to log in
          // Redirect them to the web app
          window.location.href = '/app.html?screen=auth&email=' + encodeURIComponent(credentials.email);
          return {
            success: false,
            error: 'Redirecting to student login...',
          };
        }
        
        console.error('Failed to fetch profile:', profileResponse.error);
        return {
          success: false,
          error: profileResponse.error || 'Failed to fetch user profile',
        };
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch user profile',
      };
    }
  } catch (error) {
    console.error('Login error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Login failed',
    };
  }
};

/**
 * Register new teacher account
 */
export const register = async (data: RegisterData): Promise<AuthResponse> => {
  return new Promise((resolve) => {
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: data.email }),
      new CognitoUserAttribute({ Name: 'given_name', Value: data.firstName }),
      new CognitoUserAttribute({ Name: 'family_name', Value: data.lastName }),
    ];

    userPool.signUp(
      data.email,
      data.password,
      attributeList,
      [],
      async (err, result) => {
        if (err) {
          resolve({
            success: false,
            error: err.message || 'Registration failed',
          });
          return;
        }

        if (!result) {
          resolve({
            success: false,
            error: 'Registration failed - no result',
          });
          return;
        }

        // Create teacher profile in Lambda
        const response = await apiRequest('create_teacher', {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          schoolDomain: data.schoolDomain,
        });

        if (response.success) {
          resolve({
            success: true,
            message: 'Registration successful. Please check your email to verify your account.',
          });
        } else {
          resolve({
            success: false,
            error: response.error || 'Failed to create teacher profile',
          });
        }
      }
    );
  });
};

/**
 * Initiate forgot password flow
 */
export const forgotPassword = async (email: string): Promise<AuthResponse> => {
  return new Promise((resolve) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve({
          success: true,
          message: 'Verification code sent to your email',
        });
      },
      onFailure: (err) => {
        resolve({
          success: false,
          error: err.message || 'Failed to send verification code',
        });
      },
    });
  });
};

/**
 * Reset password with verification code
 */
export const resetPassword = async (
  email: string,
  code: string,
  newPassword: string
): Promise<AuthResponse> => {
  return new Promise((resolve) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve({
          success: true,
          message: 'Password reset successful',
        });
      },
      onFailure: (err) => {
        resolve({
          success: false,
          error: err.message || 'Password reset failed',
        });
      },
    });
  });
};

/**
 * Logout current user
 */
export const logout = (): void => {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};

/**
 * Get current session
 */
export const getCurrentSession = (): Promise<AuthResponse> => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    
    if (!cognitoUser) {
      resolve({
        success: false,
        error: 'No current user',
      });
      return;
    }

    cognitoUser.getSession((err: Error | null, session: unknown) => {
      if (err || !session) {
        resolve({
          success: false,
          error: err?.message || 'No valid session',
        });
        return;
      }

      resolve({
        success: true,
        message: 'Session valid',
      });
    });
  });
};
